import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Background, Controls, Handle, MarkerType, Position, ReactFlow, type Edge, type Node, type NodeProps } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { AlertTriangle, Bot, Workflow, Wrench } from 'lucide-react';
import { useMemo, useState } from 'react';
import { ErrorNote, Loading, Toggle } from '@/components/ui';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { rk } from '@/lib/hooks';

export type GraphNode = {
  id: string;
  type: 'agent' | 'flow' | 'tool' | 'missing';
  slug: string;
  name: string;
  project: string | null;
  version: number | null;
  draftOnly: boolean;
  model: string | null;
  tools: string[];
};
export type GraphEdge = {
  id: string;
  source: string;
  target: string;
  kind: 'tool' | 'handoff' | 'step' | 'feeds' | 'uses';
  label: string;
  via?: string;
};
type Graph = { nodes: GraphNode[]; edges: GraphEdge[] };

/** How each relationship is drawn. Colour follows the relationship, never the agent. */
const EDGE_STYLE: Record<GraphEdge['kind'], { stroke: string; dash?: string; width: number; legend: string }> = {
  step: {
    stroke: 'var(--color-brand)',
    dash: '2 4',
    width: 1.5,
    legend: 'runs as a flow step',
  },
  feeds: {
    stroke: 'var(--color-brand)',
    width: 2,
    legend: 'output feeds the next agent',
  },
  tool: { stroke: 'var(--color-ink-2)', width: 2, legend: 'calls as a tool' },
  handoff: {
    stroke: 'var(--color-warn)',
    dash: '6 4',
    width: 2,
    legend: 'hands off the conversation',
  },
  uses: { stroke: 'var(--color-ink-3)', width: 1, legend: 'uses a code tool' },
};

const CARD_W = 236;
const COL_W = 350; // leaves ~110px between cards for edge labels
const ROW_H = 92;
const GRID_COLS = 4;
const TOOL_ROW_H = 34;

type CardData = { node: GraphNode; dim: boolean; focus: boolean };
type LabelData = { text: string };

const AgentCard = ({ data }: NodeProps<Node<CardData>>) => {
  const n = data.node;
  const Icon = n.type === 'flow' ? Workflow : n.type === 'missing' ? AlertTriangle : Bot;
  return (
    <div
      className={cn(
        'rounded-lg border bg-panel px-3 py-2 shadow-sm transition-opacity',
        n.type === 'missing' ? 'border-bad/60 bg-bad-soft' : data.focus ? 'border-ink ring-2 ring-ink/10' : 'border-line',
        n.type !== 'missing' && 'cursor-pointer hover:border-ink-3',
        data.dim && 'opacity-25',
      )}
      style={{ width: CARD_W }}
      title={n.type === 'missing' ? `No agent "${n.slug}" exists — a link points at it` : n.slug}
    >
      <Handle type="target" position={Position.Left} className="!size-1.5 !border-0 !opacity-0" />
      <div className="flex items-center gap-1.5">
        <Icon className={cn('size-3.5 shrink-0', n.type === 'flow' ? 'text-brand' : n.type === 'missing' ? 'text-bad' : 'text-ink-2')} />
        <span className="truncate text-[13px] font-medium">{n.name}</span>
      </div>
      <div className="mono truncate text-[11px] text-ink-3">{n.slug}</div>
      <div className="mt-1 flex flex-wrap items-center gap-1 text-[10.5px] text-ink-2">
        {n.type === 'flow' && <span className="rounded bg-brand-soft px-1 text-brand">flow</span>}
        {n.model && <span className="rounded bg-line-2 px-1">{n.model}</span>}
        {n.version ? (
          <span className="rounded bg-ok-soft px-1 text-ok">v{n.version}</span>
        ) : (
          n.type !== 'missing' && <span className="rounded bg-warn-soft px-1 text-warn">draft</span>
        )}
        {n.tools.length > 0 && (
          <span className="rounded bg-line-2 px-1" title={n.tools.join(', ')}>
            {n.tools.length} tool{n.tools.length === 1 ? '' : 's'}
          </span>
        )}
        {n.type === 'missing' && <span className="text-bad">missing agent</span>}
      </div>
      <Handle type="source" position={Position.Right} className="!size-1.5 !border-0 !opacity-0" />
    </div>
  );
};

const ToolPill = ({ data }: NodeProps<Node<CardData>>) => (
  <div className={cn('flex items-center gap-1 rounded-full border border-line bg-line-2 px-2 py-0.5 text-[11px] text-ink-2 transition-opacity', data.dim && 'opacity-25')}>
    <Handle type="target" position={Position.Left} className="!size-1 !border-0 !opacity-0" />
    <Wrench className="size-3" />
    <span className="mono">{data.node.name}</span>
  </div>
);

const GroupLabel = ({ data }: NodeProps<Node<LabelData>>) => <div className="text-[11px] font-medium tracking-wide text-ink-3 uppercase">{data.text}</div>;

const nodeTypes = { card: AgentCard, tool: ToolPill, label: GroupLabel };

/**
 * Place nodes without a layout library: each connected group is laid out left
 * to right in layers (longest path from its sources, cycles broken), groups are
 * stacked, and unconnected agents fill a grid per project underneath.
 */
const layout = (g: Graph): Map<string, { x: number; y: number }> => {
  const pos = new Map<string, { x: number; y: number }>();
  const adj = new Map<string, Set<string>>();
  const out = new Map<string, string[]>();
  for (const n of g.nodes) adj.set(n.id, new Set());
  for (const e of g.edges) {
    adj.get(e.source)?.add(e.target);
    adj.get(e.target)?.add(e.source);
    out.set(e.source, [...(out.get(e.source) ?? []), e.target]);
  }

  // Connected groups (undirected).
  const seen = new Set<string>();
  const groups: string[][] = [];
  for (const n of g.nodes) {
    if (seen.has(n.id)) continue;
    const group: string[] = [];
    const stack = [n.id];
    seen.add(n.id);
    while (stack.length) {
      const id = stack.pop()!;
      group.push(id);
      for (const m of adj.get(id) ?? []) if (!seen.has(m)) (seen.add(m), stack.push(m));
    }
    groups.push(group);
  }
  const connected = groups.filter((gr) => gr.length > 1).sort((a, b) => b.length - a.length);
  const single = groups.filter((gr) => gr.length === 1).map((gr) => gr[0]);

  let top = 0;
  for (const group of connected) {
    const inGroup = new Set(group);
    // Break cycles: DFS, ignore edges back into the current path.
    const dag = new Map<string, string[]>();
    const state = new Map<string, 1 | 2>();
    const visit = (id: string) => {
      state.set(id, 1);
      for (const t of out.get(id) ?? []) {
        if (!inGroup.has(t) || state.get(t) === 1) continue;
        dag.set(id, [...(dag.get(id) ?? []), t]);
        if (!state.has(t)) visit(t);
      }
      state.set(id, 2);
    };
    const hasIncoming = new Set(g.edges.filter((e) => inGroup.has(e.source)).map((e) => e.target));
    for (const id of [...group.filter((id) => !hasIncoming.has(id)), ...group]) if (!state.has(id)) visit(id);
    // Longest path layering.
    const layer = new Map<string, number>(group.map((id) => [id, 0]));
    for (let pass = 0; pass < group.length; pass++) {
      let changed = false;
      for (const [s, ts] of dag) for (const t of ts) if (layer.get(t)! < layer.get(s)! + 1) (layer.set(t, layer.get(s)! + 1), (changed = true));
      if (!changed) break;
    }
    const rows = new Map<number, string[]>();
    for (const id of group) rows.set(layer.get(id)!, [...(rows.get(layer.get(id)!) ?? []), id]);
    // Tool pills are short, so a column of tools doesn't stretch the map like a column of cards would.
    const rowH = (id: string) => (id.startsWith('tool:') ? TOOL_ROW_H : ROW_H);
    const colH = (ids: string[]) => ids.reduce((sum, id) => sum + rowH(id), 0);
    const height = Math.max(...[...rows.values()].map(colH));
    for (const [l, ids] of rows) {
      let y = top + (height - colH(ids)) / 2;
      for (const id of ids) {
        pos.set(id, { x: l * COL_W, y });
        y += rowH(id);
      }
    }
    top += height + 48;
  }

  // Unconnected agents: a grid per project.
  const byProject = new Map<string, string[]>();
  const nodeById = new Map(g.nodes.map((n) => [n.id, n]));
  for (const id of single) {
    const p = nodeById.get(id)?.project ?? 'other';
    byProject.set(p, [...(byProject.get(p) ?? []), id]);
  }
  for (const [project, ids] of [...byProject].sort(([a], [b]) => a.localeCompare(b))) {
    pos.set(`label:${project}`, { x: 0, y: top });
    top += 28;
    ids.forEach((id, i) =>
      pos.set(id, {
        x: (i % GRID_COLS) * (CARD_W + 28),
        y: top + Math.floor(i / GRID_COLS) * ROW_H,
      }),
    );
    top += Math.ceil(ids.length / GRID_COLS) * ROW_H + 24;
  }
  return pos;
};

/** Only `focusId` and what it's directly linked to (for an agent's own page). */
const neighbourhood = (g: Graph, focusId: string): Graph => {
  const keep = new Set([focusId]);
  for (const e of g.edges) if (e.source === focusId || e.target === focusId) (keep.add(e.source), keep.add(e.target));
  return {
    nodes: g.nodes.filter((n) => keep.has(n.id)),
    edges: g.edges.filter((e) => keep.has(e.source) && keep.has(e.target)),
  };
};

export const AgentMap = ({ project, focusId, height = 720 }: { project?: string; focusId?: string; height?: number }) => {
  const navigate = useNavigate();
  const [showTools, setShowTools] = useState(false);
  const [hover, setHover] = useState<string | null>(null);
  const q = useQuery({
    queryKey: rk('agent-graph', project ?? 'all', showTools),
    queryFn: () => api<Graph>(`/v1/agents/graph?${new URLSearchParams({ ...(project ? { project } : {}), ...(showTools ? { tools: 'true' } : {}) })}`),
  });

  const graph = useMemo(() => (q.data && focusId ? neighbourhood(q.data, focusId) : q.data), [q.data, focusId]);
  const links = graph?.edges.filter((e) => e.kind !== 'uses').length ?? 0;

  const { nodes, edges } = useMemo(() => {
    if (!graph) return { nodes: [] as Node[], edges: [] as Edge[] };
    const pos = layout(graph);
    const lit = new Set<string>();
    if (hover) {
      lit.add(hover);
      for (const e of graph.edges) if (e.source === hover || e.target === hover) (lit.add(e.source), lit.add(e.target));
    }
    const dim = (id: string) => Boolean(hover) && !lit.has(id);
    const labels: Node[] = [...pos.keys()]
      .filter((k) => k.startsWith('label:'))
      .map((k) => ({
        id: k,
        type: 'label',
        position: pos.get(k)!,
        data: { text: `${k.slice(6)} · not connected` },
        draggable: false,
        selectable: false,
      }));
    return {
      nodes: [
        ...labels,
        ...graph.nodes.map<Node>((n) => ({
          id: n.id,
          type: n.type === 'tool' ? 'tool' : 'card',
          position: pos.get(n.id) ?? { x: 0, y: 0 },
          data: {
            node: n,
            dim: dim(n.id),
            focus: n.id === focusId,
          } satisfies CardData,
        })),
      ],
      edges: graph.edges.map<Edge>((e) => {
        const s = EDGE_STYLE[e.kind];
        const faded = Boolean(hover) && e.source !== hover && e.target !== hover;
        return {
          id: e.id,
          source: e.source,
          target: e.target,
          // Step labels crowd where flows share agents, so they appear on hover.
          label: e.kind === 'uses' || e.kind === 'feeds' || (e.kind === 'step' && hover !== e.source && hover !== e.target) ? undefined : e.label,
          labelStyle: { fontSize: 10.5, fill: 'var(--color-ink-2)' },
          labelBgStyle: { fill: 'var(--color-panel)' },
          style: {
            stroke: s.stroke,
            strokeWidth: s.width,
            strokeDasharray: s.dash,
            opacity: faded ? 0.12 : 1,
          },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: s.stroke,
            width: 14,
            height: 14,
          },
                  };
      }),
    };
  }, [graph, hover, focusId]);

  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorNote error={q.error} />;
  if (!graph) return null;
  const self = focusId ? graph.nodes.find((n) => n.id === focusId) : undefined;
  if (focusId && graph.edges.length === 0)
    return (
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 text-[13px] text-ink-2">
        <span>
          Not linked to any other agent — product code calls it directly. Links show here when it runs in a flow, calls another agent as a tool, or hands off.
          {self?.tools.length ? ` It can use ${self.tools.length} code tool${self.tools.length === 1 ? '' : 's'}.` : ''}
        </span>
        {self?.tools.length ? <Toggle checked={showTools} onChange={setShowTools} label="Show code tools" /> : null}
      </div>
    );

  return (
    <div className="flex flex-col">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-line px-4 py-2.5 text-[12px] text-ink-2">
        {(Object.keys(EDGE_STYLE) as Array<GraphEdge['kind']>)
          .filter((k) => k !== 'uses' || showTools)
          .map((k) => (
            <span key={k} className="flex items-center gap-1.5">
              <svg width="26" height="8" aria-hidden>
                <line x1="1" y1="4" x2="25" y2="4" stroke={EDGE_STYLE[k].stroke} strokeWidth={EDGE_STYLE[k].width} strokeDasharray={EDGE_STYLE[k].dash} />
              </svg>
              {EDGE_STYLE[k].legend}
            </span>
          ))}
        <span className="ml-auto flex items-center gap-4">
          {!focusId && (
            <span className="text-ink-3">
              {graph.nodes.filter((n) => n.type === 'agent' || n.type === 'flow').length} agents · {links} link{links === 1 ? '' : 's'}
            </span>
          )}
          <Toggle checked={showTools} onChange={setShowTools} label="Show code tools" />
        </span>
      </div>
      {!focusId && links === 0 && (
        <p className="border-b border-line bg-line-2/50 px-4 py-2 text-[12px] text-ink-2">
          No agents are linked yet — each is called directly by product code. Links appear here when a flow runs an agent, an agent calls another as a tool, or hands off to one.
        </p>
      )}
      <div style={{ height }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.1, maxZoom: 1, minZoom: 0.55 }}
          minZoom={0.2}
          nodesConnectable={false}
          proOptions={{ hideAttribution: true }}
          onNodeMouseEnter={(_e, n) => n.type !== 'label' && setHover(n.id)}
          onNodeMouseLeave={() => setHover(null)}
          onNodeClick={(_e, n) => {
            const gn = (n.data as CardData | undefined)?.node;
            if (gn && (gn.type === 'agent' || gn.type === 'flow') && gn.id !== focusId)
              void navigate({
                to: '/agents/$agentId',
                params: { agentId: gn.id },
              });
          }}
        >
          <Background gap={20} size={1} color="var(--color-line)" />
          <Controls showInteractive={false} />
        </ReactFlow>
      </div>
    </div>
  );
};
